import React from 'react';

import type { ITableColumn, ITableRow } from './Types';
import moment, { type Moment } from 'moment';

import type { SortOrder } from '@cobliteam/common-types';

export class TableSorter {
  public static sort = <T extends ITableRow>(
    rows: T[],
    order: SortOrder,
    column: ITableColumn<T>
  ) => {
    const comparingFunction = TableSorter.getSorting(order, column);
    return rows.slice().sort(comparingFunction);
  };

  private static getSorting = <T extends ITableRow>(
    order: SortOrder,
    column: ITableColumn<T>
  ) => {
    return order === 'desc'
      ? (a: T, b: T) => TableSorter.desc(a, b, column)
      : (a: T, b: T) => -TableSorter.desc(a, b, column);
  };

  private static desc = <T extends ITableRow>(
    rowA: T,
    rowB: T,
    column: ITableColumn<T>
  ) => {
    const a = React.isValidElement(column.Cell(rowA))
      ? column.orderByKey?.(rowA) || ''
      : column.Cell(rowA) || '';

    const b = React.isValidElement(column.Cell(rowB))
      ? column.orderByKey?.(rowB) || ''
      : column.Cell(rowB) || '';

    const { areValidDates, firstDate, secondDate } = TableSorter.dateCheck(
      a.toString(),
      b.toString()
    );

    if (areValidDates) {
      return TableSorter.dateComparison(firstDate, secondDate);
    }

    if (a > b) {
      return -1;
    }

    if (b > a) {
      return 1;
    }

    return 0;
  };

  private static dateComparison = (firstDate: Moment, secondDate: Moment) => {
    if (moment(firstDate).isAfter(secondDate)) {
      return 1;
    }

    if (moment(firstDate).isBefore(secondDate)) {
      return -1;
    }
    return 0;
  };

  private static dateCheck = (a: string, b: string) => {
    const firstDate = moment(a, 'DD/MM/YYYY', true);
    const secondDate = moment(b, 'DD/MM/YYYY', true);

    return {
      areValidDates:
        moment(firstDate).isValid() && moment(secondDate).isValid(),
      firstDate,
      secondDate,
    };
  };
}
